import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { OrderSchemaType, ItemPath } from '../order.schema';

interface UseFeedstockQuantityAutoFillArgs {
  /** Path do sub-item de matéria-prima (`items.N.subItems.M`). */
  itemPath: ItemPath;
}

const toNum = (value: any): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Matéria-prima vinda do roteiro acompanha a quantidade do item pai, na unidade soberana do
 * item (em KG, é o peso). Mesma regra da Especificação e do Peso: escreve sobre vazio ou sobre o
 * último valor que ela mesma escreveu; valor digitado vence; limpar reativa. Sub-item adicionado
 * à mão nunca é tocado.
 */
export const useFeedstockQuantityAutoFill = ({ itemPath }: UseFeedstockQuantityAutoFillArgs): { isQuantityAuto: boolean } => {
  const { control, getValues, setValue } = useFormContext<OrderSchemaType>();
  const lastAutoRef = useRef<number | null>(null);

  const isSubItem = itemPath.includes('.subItems.');
  const parentPath = itemPath.replace(/\.subItems\.\d+$/, '');

  const fromScript = useWatch({ control, name: `${itemPath}.fromScript` as any }) as boolean | undefined;
  const parentQuantity = useWatch({ control, name: `${parentPath}.quantity` as any });
  const quantidade = useWatch({ control, name: `${itemPath}.feedstock.quantidade` as any });

  const enabled = isSubItem && fromScript === true;
  const auto = toNum(parentQuantity);

  useEffect(() => {
    if (!enabled) return;
    const path = `${itemPath}.feedstock.quantidade` as any;
    const opts = { shouldDirty: true, shouldValidate: false };
    const current = toNum(getValues(path));
    const ownedByCalc = current === null || current === lastAutoRef.current;
    if (auto === null || auto <= 0) {
      if (current !== null && current === lastAutoRef.current) setValue(path, '', opts);
      lastAutoRef.current = null;
      return;
    }
    if (ownedByCalc) {
      if (current !== auto) setValue(path, auto, opts);
      lastAutoRef.current = auto;
    }
  }, [enabled, auto, quantidade, itemPath, getValues, setValue]);

  const now = toNum(quantidade);
  return { isQuantityAuto: enabled && auto !== null && now !== null && now === lastAutoRef.current };
};
